import { Box, Button, Divider, Menu, ScrollArea, Tooltip, type ButtonProps } from "@mantine/core";
import { IDocxTemplate } from "@mctypes/global.types";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { pathIsInIssueApp } from "../../app/routes/issuesApp/issueUtils";

const DocxReportDropdown: React.FC<{
  docxTemplates: IDocxTemplate[];
  generateDocx: (docxTemplate: IDocxTemplate) => void;
  openDocxModal: () => void;
  buttonColor?: ButtonProps["color"];
  buttonVariant?: ButtonProps["variant"];
  buttonSize?: ButtonProps["size"];
  buttonPx?: ButtonProps["px"];
  buttonFw?: ButtonProps["fw"];
  buttonText?: string;
}> = ({
  docxTemplates,
  generateDocx,
  openDocxModal,
  buttonColor,
  buttonVariant,
  buttonSize,
  buttonPx,
  buttonFw,
  buttonText,
}) => {
  const [templates, setTemplates] = useState<IDocxTemplate[][]>([[], [], []]);

  const history = useHistory();
  const location = history.location;

  const urlIsIssueApp = pathIsInIssueApp(location.pathname);

  const init = () => {
    const checklistGroups: IDocxTemplate[][] = [[], [], []];

    docxTemplates.forEach((template) => {
      if (urlIsIssueApp === true) {
        switch (template.templateLevel) {
          case "PROJECT":
            checklistGroups[0].push(template);
            break;
          case "MULTICONSULT":
            checklistGroups[1].push(template);
            break;
          case "BOARD":
            checklistGroups[2].push(template);
            break;
          default:
            break;
        }
      } else {
        if (template.isMulticonsult !== true) {
          checklistGroups[0].push(template);
        } else {
          checklistGroups[1].push(template);
        }
      }
    });

    setTemplates(checklistGroups);
  };

  useEffect(() => {
    init();
  }, [docxTemplates]);

  return (
    <Menu width={280} shadow="md" withArrow={false} position="bottom-start" closeOnItemClick withinPortal>
      <Tooltip label="Eksporter til Word" withinPortal withArrow offset={2} openDelay={200}>
        <Menu.Target>
          <Button
            variant={buttonVariant ?? "secondary-filled"}
            color={buttonColor}
            size={buttonSize ?? "sm"}
            px={buttonPx}
            pl={buttonPx == null ? "xs" : undefined}
            fw={buttonFw}
            leftSection={<i className="fa fa-file-word-o fa-fw" style={{ fontSize: "0.9rem" }} />}
            rightSection={<i className="fa fa-caret-down" />}
          >
            {buttonText ?? "Lag møtereferat"}
          </Button>
        </Menu.Target>
      </Tooltip>
      <Menu.Dropdown>
        <ScrollArea style={{ maxHeight: "200px", overflowY: "auto" }} type="auto">
          <Box>
            {templates[0]?.length > 0 && (
              <DropdownGroup templates={templates[0]} generateDocx={generateDocx} groupTitle="Prosjekt" />
            )}
            {templates[1]?.length > 0 && (
              <DropdownGroup templates={templates[1]} generateDocx={generateDocx} groupTitle="Multiconsult" />
            )}
            {templates[2]?.length > 0 && (
              <DropdownGroup templates={templates[2]} generateDocx={generateDocx} groupTitle="Register" />
            )}
            {(templates[0].length > 0 || templates[1].length > 0) && <Divider my={6} />}
            <Menu.Item
              leftSection={<i className="fa fa-cog fa-fw" />}
              onClick={() => {
                openDocxModal();
              }}
            >
              Innstillinger
            </Menu.Item>
          </Box>
        </ScrollArea>
      </Menu.Dropdown>
    </Menu>
  );
};

export default DocxReportDropdown;

const DropdownGroup: React.FC<{
  templates: IDocxTemplate[];
  generateDocx: (t: IDocxTemplate) => void;
  groupTitle: string;
}> = ({ templates, generateDocx, groupTitle }) => {
  if (!templates?.length) return null;
  return (
    <Box mb={8}>
      <Menu.Label>{groupTitle}</Menu.Label>
      {templates.map((t) => (
        <Menu.Item
          key={t.fileName}
          leftSection={<i className="fa fa-file-word-o fa-fw" />}
          onClick={() => generateDocx(t)}
        >
          <span className="truncateHeaderText">{t.fileName}</span>
        </Menu.Item>
      ))}
    </Box>
  );
};
